import { useState, useEffect } from "react";

import { format } from "date-fns";
import { RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";




interface RecoveryAttempt {
  id: string;
  original_attempt_id: string;
  retry_number: number;
  status: string;
  recovered_from_step: string | null;
  error_message: string | null;
  time_to_recovery_seconds: number | null;
  next_retry_at: string | null;
  created_at: string;
  original_attempt?: {
    board_type: string;
    step: string;
    user_id: string;
  };
}

const RecoveryHistoryTimeline = () => {
  const [attempts, setAttempts] = useState<RecoveryAttempt[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFailureId, setSelectedFailureId] = useState<string | null>(null);

  const fetchRecoveryHistory = async (failureId?: string) => {
    setLoading(true);
    try {
      let query = supabase
        .from("board_creation_recovery_attempts")
        .select(`
          *,
          original_attempt:board_creation_attempts!original_attempt_id(
            board_type,
            step,
            user_id
          )
        `)
        .order("created_at", { ascending: false });

      if (failureId) {
        query = query.eq("original_attempt_id", failureId);
      } else {
        query = query.limit(50);
      }

      const { data, error } = await query;

      if (error) throw error;
      setAttempts(data || []);
    } catch (error) {
      console.error("Error fetching recovery history:", error);
      toast.error("Failed to load recovery history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecoveryHistory();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case "failure":
        return <XCircle className="h-5 w-5 text-destructive" />;
      case "skipped":
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "success":
        return "border-green-600 bg-green-50 dark:bg-green-950";
      case "failure":
        return "border-destructive bg-destructive/5";
      case "skipped":
        return "border-yellow-600 bg-yellow-50 dark:bg-yellow-950";
      default:
        return "border-border";
    }
  };

  const getStepLabel = (step: string) => {
    const labels: Record<string, string> = {
      workspace_fetch: "Workspace Fetch",
      board_create: "Board Creation",
      personas_create: "Personas Creation",
      project_create: "Project Creation",
      session_create: "Session Creation",
    };
    return labels[step] || step;
  };

  const groupedAttempts = attempts.reduce((acc, attempt) => {
    const failureId = attempt.original_attempt_id;
    if (!acc[failureId]) {
      acc[failureId] = [];
    }
    acc[failureId].push(attempt);
    return acc;
  }, {} as Record<string, RecoveryAttempt[]>);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Recovery History Timeline</CardTitle>
            <CardDescription>
              Detailed log of all recovery attempts with timestamps and outcomes
            </CardDescription>
          </div>
          <Button
            onClick={() => fetchRecoveryHistory(selectedFailureId || undefined)}
            disabled={loading}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[600px] pr-4">
          {Object.entries(groupedAttempts).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Clock className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                No recovery attempts found. Recovery attempts will appear here once the system starts retrying failed board creations.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedAttempts).map(([failureId, failureAttempts]) => {
                const sortedAttempts = [...failureAttempts].sort(
                  (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                );
                const firstAttempt = sortedAttempts[0];
                const originalAttempt = firstAttempt.original_attempt;

                return (
                  <div key={failureId} className="space-y-4">
                    <div className="flex items-center gap-4 pb-2 border-b">
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm">
                          Original Failure: {getStepLabel(originalAttempt?.step || "unknown")}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {originalAttempt?.board_type} • {sortedAttempts.length} recovery attempts
                        </p>
                      </div>
                      <Badge variant="outline">
                        {failureId.slice(0, 8)}
                      </Badge>
                    </div>

                    <div className="relative pl-8 space-y-4">
                      {/* Timeline line */}
                      <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-border" />

                      {sortedAttempts.map((attempt, index) => (
                        <div key={attempt.id} className="relative">
                          {/* Timeline dot */}
                          <div className="absolute -left-8 mt-1">
                            {getStatusIcon(attempt.status)}
                          </div>

                          <div className={`border-l-4 p-4 rounded-lg ${getStatusColor(attempt.status)}`}>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant={attempt.status === "success" ? "default" : "secondary"}>
                                    Retry #{attempt.retry_number}
                                  </Badge>
                                  <Badge variant={
                                    attempt.status === "success" 
                                      ? "default" 
                                      : attempt.status === "failure" 
                                      ? "destructive" 
                                      : "secondary"
                                  }>
                                    {attempt.status}
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {format(new Date(attempt.created_at), "MMM d, yyyy 'at' h:mm:ss a")}
                                </p>
                              </div>
                            </div>

                            {attempt.status === "success" && (
                              <div className="mt-3 space-y-1">
                                <p className="text-sm">
                                  <span className="font-medium">✓ Recovered from:</span>{" "}
                                  {getStepLabel(attempt.recovered_from_step || "unknown")}
                                </p>
                                {attempt.time_to_recovery_seconds && (
                                  <p className="text-sm">
                                    <span className="font-medium">⏱️ Recovery time:</span>{" "}
                                    {attempt.time_to_recovery_seconds}s
                                  </p>
                                )}
                              </div>
                            )}

                            {attempt.status === "failure" && (
                              <div className="mt-3 space-y-1">
                                {attempt.error_message && (
                                  <p className="text-sm">
                                    <span className="font-medium text-destructive">✗ Error:</span>{" "}
                                    <span className="text-muted-foreground">{attempt.error_message}</span>
                                  </p>
                                )}
                                {attempt.next_retry_at && (
                                  <p className="text-sm">
                                    <span className="font-medium">🔄 Next retry:</span>{" "}
                                    {format(new Date(attempt.next_retry_at), "MMM d, yyyy 'at' h:mm a")}
                                  </p>
                                )}
                              </div>
                            )}

                            {attempt.status === "skipped" && attempt.error_message && (
                              <div className="mt-3">
                                <p className="text-sm">
                                  <span className="font-medium text-yellow-600">⚠ Skipped:</span>{" "}
                                  <span className="text-muted-foreground">{attempt.error_message}</span>
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default RecoveryHistoryTimeline;
